import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, EmptyState, Select, Spinner, TextField, Typography } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { adminApiClient, type OrderReview, type OrderReviewOutcome } from '../lib/adminApiClient';
import { NEUTRAL_BTN, DANGER_BTN } from '../lib/buttonStyles';
import { Pill } from '../components/Pill';
import { holdTimeLeft, URGENCY_COLORS } from '../lib/holdTimeLeft';

/**
 * Order reviews (Rod, 2026-09-27; Figma Desktop Playground DE28 1960:574).
 *
 * Every reported problem and every Runner safety exit waits here. The
 * customer's charge and the Runner's pay are held until someone decides; the
 * money moves within 15 minutes of the decision.
 */
type Filter = 'open' | 'decided';

const money = (c: number) => `$${(c / 100).toFixed(2)}`;
const at = (iso: string) => new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const time = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

const KIND: Record<OrderReview['kind'], { label: string; fg: string; bg: string }> = {
  safety: { label: 'SAFETY EXIT', fg: '#b42318', bg: '#fee4e2' },
  couldnt_deliver: { label: 'COULDN’T DELIVER', fg: '#A85200', bg: '#fff4e5' },
  delivered: { label: 'DELIVERED', fg: '#343F5B', bg: '#eef2f6' },
};

const REASON: Record<string, string> = {
  not_delivered: 'I was home — nobody came to the door',
  missing_or_wrong: 'Items missing or wrong',
  damaged: 'Something was damaged or spoiled',
  other: 'Something else',
  runner_safety: 'Runner left for their safety',
};

const OUTCOME: Record<OrderReviewOutcome, string> = {
  charge_pay: 'Customer charged · Runner paid',
  refund_pay: 'Customer refunded · Runner paid',
  refund_no_pay: 'Customer refunded · Runner not paid',
};

function timeline(r: OrderReview): Array<{ at: string; text: string; flag?: boolean }> {
  const e = r.evidence;
  if (!e) return [];
  const rows: Array<{ at: string; text: string; flag?: boolean }> = [];
  if (e.doorWaitStartedAt) {
    rows.push({ at: e.doorWaitStartedAt, text: `Tapped “Nobody home?” — customer messaged, 2-minute wait started${e.doorWaitLocation ? '' : ' · no location recorded'}`, flag: !e.doorWaitLocation });
  }
  if (e.doorReply && e.doorReplyAt) rows.push({ at: e.doorReplyAt, text: e.doorReply === 'leave' ? 'Customer replied “Leave it at the door”' : 'Customer replied “I’m coming”' });
  if (e.retryPendingAt) rows.push({ at: e.retryPendingAt, text: 'No safe place — moved to the end of the run for one return attempt' });
  if (e.deliveredAt) rows.push({ at: e.deliveredAt, text: `Delivered${e.exceptionCode === 'left_at_door' ? ', left at the door' : ''}${e.deliveryLocation ? '' : ' · no location recorded'}`, flag: !e.deliveryLocation });
  if (e.undeliveredAt) {
    rows.push({
      at: e.undeliveredAt,
      text: `${e.undeliveredReason === 'safety' ? 'Left for safety' : 'Marked couldn’t deliver'}${e.undeliveredReason === 'nobody_home' && !e.undeliveredLocation ? ' · no location recorded' : ''}${e.exceptionNote ? ` — “${e.exceptionNote}”` : ''}`,
      flag: e.undeliveredReason === 'nobody_home' && !e.undeliveredLocation,
    });
  }
  if (e.donatedAt) rows.push({ at: e.donatedAt, text: `Groceries donated${e.donationNote ? ` — ${e.donationNote}` : ''}` });
  return rows.sort((a, b) => a.at.localeCompare(b.at));
}

export default function OrderReviews() {
  const caps = useCapabilities();
  const canView = caps.has('user:read');
  const canDecide = caps.has('user:manage');
  const [filter, setFilter] = useState<Filter>('open');
  const [reviews, setReviews] = useState<OrderReview[]>([]);
  const [now] = useState(() => new Date());
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listOrderReviews(filter);
      setReviews(res.reviews);
      setOpenCount(res.counts.open);
      setSelected((cur) => (cur && res.reviews.some((r) => r.orderId === cur) ? cur : res.reviews[0]?.orderId ?? null));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  const decide = async (r: OrderReview, outcome: OrderReviewOutcome) => {
    if (!note.trim()) {
      setError('Add a note first — it is the record of why.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await adminApiClient.decideOrderReview(r.orderId, { outcome, note: note.trim() });
      setInfo(`${OUTCOME[outcome]}. The money moves within 15 minutes and the customer is emailed.`);
      setNote('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not save');
    } finally {
      setBusy(false);
    }
  };

  if (caps.loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><Spinner color="brand" /></Box>;
  if (!canView) return <Alert severity="error">You don’t have permission to view order reviews.</Alert>;

  const current = reviews.find((r) => r.orderId === selected) ?? null;
  const customer = current?.household.name?.split(' ')[0] ?? 'the customer';
  const runner = current?.runnerName?.split(' ')[0] ?? 'the Runner';

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Order reviews</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Reported problems and Runner safety exits. The customer’s charge and the Runner’s pay are held until you
        decide. A couldn’t-deliver order is never charged the tip.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Select
          ariaLabel="Filter reviews" label="Show" native size="small" sx={{ width: 280 }} value={filter}
          onChange={(_e, v) => setFilter(v as Filter)}
          options={[
            { value: 'open', label: `Waiting for a decision (${openCount})` },
            { value: 'decided', label: 'Decided' },
          ]}
        />
      </Box>

      {error && <Box sx={{ mb: 2 }}><Alert severity="error">{error}</Alert></Box>}
      {info && <Box sx={{ mb: 2 }}><Alert severity="success">{info}</Alert></Box>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><Spinner color="brand" /></Box>
      ) : reviews.length === 0 ? (
        <EmptyState
          icon="onlineordering"
          title={filter === 'open' ? 'Nothing is waiting on a decision' : 'Nothing here'}
          body={filter === 'open' ? 'No reported problems or safety exits right now.' : 'No decided reviews yet.'}
        />
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, gap: 2 }}>
          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', backgroundColor: '#fff', alignSelf: 'start' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '0.9fr 0.7fr 1.2fr 1fr 1.1fr', gap: 2, px: 2, py: 1.25, backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              {['Order', 'Hold left', 'What happened', 'Customer', 'Runner'].map((h) => (
                <Typography key={h} variant="caption" sx={{ fontWeight: 700, color: '#64748b', letterSpacing: 0.4 }}>{h.toUpperCase()}</Typography>
              ))}
            </Box>
            {reviews.map((r) => {
              const noLocation = timeline(r).some((t) => t.flag);
              return (
                <Box
                  key={r.orderId}
                  component="button"
                  type="button"
                  onClick={() => { setSelected(r.orderId); setInfo(null); }}
                  aria-pressed={r.orderId === selected}
                  sx={{
                    display: 'grid', gridTemplateColumns: '0.9fr 0.7fr 1.2fr 1fr 1.1fr', gap: 2, px: 2, py: 1.5, width: '100%',
                    textAlign: 'left', border: 'none', borderBottom: '1px solid #eef2f6', cursor: 'pointer', fontFamily: 'inherit',
                    backgroundColor: r.orderId === selected ? '#f1f5f9' : '#fff', '&:hover': { backgroundColor: '#fafbfc' },
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: '#1f2937' }}>{r.ref}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{r.reportedAt ? at(r.reportedAt) : '—'}</Typography>
                  </Box>
                  <Box data-testid={`review-hold-left-${r.orderId}`}>
                    {(() => {
                      const left = r.reviewOutcome ? null : holdTimeLeft(r.holdExpiresAt, now);
                      return left
                        ? <Pill label={left.label} fg={URGENCY_COLORS[left.urgency].fg} bg={URGENCY_COLORS[left.urgency].bg} />
                        : <Typography variant="caption" sx={{ color: 'text.secondary' }}>—</Typography>;
                    })()}
                  </Box>
                  <Box>
                    <Pill label={KIND[r.kind].label} fg={KIND[r.kind].fg} bg={KIND[r.kind].bg} />
                    {noLocation && <Typography variant="caption" sx={{ display: 'block', color: '#b42318', fontWeight: 700, mt: 0.5 }}>⚠ no location</Typography>}
                  </Box>
                  <Typography variant="body2">{r.household.name ?? r.household.glpUserId}</Typography>
                  <Box>
                    <Typography variant="body2">{r.runnerName ?? '—'}</Typography>
                    {r.runnerStats && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {r.runnerStats.couldNotDeliver} of {r.runnerStats.total} couldn’t deliver · 90 days
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {current && (
            <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 2, backgroundColor: '#fff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 700, color: '#1f2937' }}>{current.ref} · {current.household.name ?? current.household.glpUserId}</Typography>
                <Pill label={KIND[current.kind].label} fg={KIND[current.kind].fg} bg={KIND[current.kind].bg} />
              </Box>
              <Typography variant="body2" sx={{ color: '#1f2937', mb: 1.5 }}>
                {REASON[current.reportReason ?? 'other'] ?? current.reportReason}
                {current.reportNote ? ` — “${current.reportNote}”` : ''}
              </Typography>

              {!current.evidence ? (
                <Alert severity="warning">Couldn’t load what happened at the door from the Runner service. Try again.</Alert>
              ) : (
                <Box sx={{ mb: 1.5 }}>
                  {timeline(current).map((t) => (
                    <Box key={t.at + t.text} sx={{ display: 'flex', gap: 1.5, py: 0.75, borderBottom: '1px solid #eef2f6' }}>
                      <Typography variant="caption" sx={{ width: 64, flexShrink: 0, color: 'text.secondary' }}>{time(t.at)}</Typography>
                      <Typography variant="body2" sx={{ color: t.flag ? '#b42318' : '#1f2937', fontWeight: t.flag ? 600 : 400 }}>{t.text}</Typography>
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.25, flexWrap: 'wrap' }}>
                    {[
                      { label: 'Delivery photo', url: current.evidence.deliveryPhoto },
                      { label: 'Door photo', url: current.evidence.doorPhoto },
                      { label: 'Donation photo', url: current.evidence.donationPhoto },
                    ].filter((p) => p.url).map((p) => (
                      <Box key={p.label} component="a" href={p.url as string} target="_blank" rel="noreferrer" sx={{ textDecoration: 'none' }}>
                        <Box component="img" src={p.url as string} alt={p.label} sx={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 1.5, border: '1px solid #e2e8f0', display: 'block' }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{p.label}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {current.runnerStats && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1.5 }}>
                  {current.runnerName ?? 'Runner'}: {current.runnerStats.couldNotDeliver} couldn’t deliver and {current.runnerStats.safetyExits} safety exit{current.runnerStats.safetyExits === 1 ? '' : 's'} in {current.runnerStats.total} orders over 90 days
                  ({Math.round(current.runnerStats.couldNotDeliverRate * 100)}%).
                </Typography>
              )}

              {current.reviewOutcome ? (
                <Typography variant="body2" sx={{ color: '#1f2937' }}>
                  Decided: {OUTCOME[current.reviewOutcome]}{current.reviewStatus === 'open' ? ' — money moving' : ''}.
                  {current.reviewNote ? ` Note: ${current.reviewNote}` : ''}
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937', mb: 1.25 }}>
                    Held: {money(current.heldCents)} from {customer} · $7.00{current.tipCents ? ` + ${money(current.tipCents)} tip` : ''} for {runner}
                  </Typography>
                  {canDecide && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                      <TextField label="Note (required)" value={note} onChange={(ev) => setNote(ev.target.value)} size="small" multiline fullWidth />
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button ariaLabel={`Charge ${customer} and pay ${runner}`} buttonText={`Charge ${customer} · pay ${runner}`} variant="contained" color="primary" disabled={busy} onClick={() => void decide(current, 'charge_pay')} />
                        <Button ariaLabel={`Refund ${customer} and pay ${runner}`} buttonText={`Refund ${customer} · pay ${runner}`} variant="outlined" sx={NEUTRAL_BTN} disabled={busy} onClick={() => void decide(current, 'refund_pay')} />
                        {current.kind !== 'safety' && (
                          <Button ariaLabel={`Refund ${customer} and don’t pay ${runner}`} buttonText={`Refund ${customer} · don’t pay ${runner}`} variant="outlined" sx={DANGER_BTN} disabled={busy} onClick={() => void decide(current, 'refund_no_pay')} />
                        )}
                      </Box>
                      {current.kind === 'safety' && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>A Runner who left for their safety is always paid.</Typography>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
